<script setup lang="ts">
import {ref} from "vue";
import {useResizeObserver} from "@vueuse/core";
import {ChevronDown} from "@lucide/vue";

const viewportRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const canScrollDown = ref(false);

/**
 * Determine whether there is more content below the visible area.
 */
const updateScrollState = () => {
    const el = viewportRef.value;

    if (!el) {
        canScrollDown.value = false;
        return;
    }

    canScrollDown.value = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
};

useResizeObserver(viewportRef, updateScrollState);
useResizeObserver(contentRef, updateScrollState);

const scrollDown = () => {
    const el = viewportRef.value;

    if (!el) {
        return;
    }

    el.scrollBy({top: el.clientHeight * 0.8, behavior: "smooth"});
};
</script>

<template>
  <div class="relative flex-1 min-h-0">
    <div
      ref="viewportRef"
      class="h-full overflow-y-auto no-scrollbar"
      @scroll.passive="updateScrollState"
    >
      <div ref="contentRef">
        <slot />
      </div>
    </div>
    <Transition name="fade">
      <div
        v-if="canScrollDown"
        class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center"
      >
        <div class="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/80 to-transparent" />
        <button
          type="button"
          class="pointer-events-auto relative mb-1 rounded-full bg-white shadow p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Faire défiler vers le bas"
          @click="scrollDown"
        >
          <ChevronDown class="w-4 h-4 bounce-subtle" />
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.no-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.bounce-subtle {
    animation: bounce-subtle 1.5s ease-in-out infinite;
}

@keyframes bounce-subtle {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(2px);
    }
}
</style>
